/**
 * TypeScript shape of a row in the tenant `KboFlowRuns` table as returned by
 * `KboFlowDebugController`. The `runOutput` field is the parsed JSON of the serialized
 * `Kbo.Flow` run (camelCase). Heavy / deeply-nested context sub-objects are kept loose
 * because they are surfaced via the raw JSON viewer rather than rendered field-by-field.
 */
export interface KboFlowRunDebug {
    id: string;
    subjectUserId: string;
    version: number;
    createdOn: string;
    outcomeMessage?: string | null;
    /** Persisted run outcome; this is authoritative when present. */
    status?: string | null;
    /** Parsed run output, or the raw string if it could not be parsed server-side. */
    runOutput?: KboFlowRunOutput | string | null;
    generatedReportCount: number;
    failedExplorationNodeCount: number;
}

/**
 * Lightweight, flat summary of a run for the paginated list (no `runOutput`). The derived fields
 * are computed server-side and drive the collapsed row's chips.
 */
export interface KboFlowRunSummary {
    id: string;
    subjectUserId: string;
    version: number;
    createdOn: string;
    outcomeMessage?: string | null;
    /** Persisted run outcome; this is authoritative when present. */
    status?: string | null;
    runId?: string | null;
    mode?: string | null;
    startedAtUtc?: string | null;
    completedAtUtc?: string | null;
    generatedQuestionCount: number;
    generatedReportCount: number;
    analysisFindingCount: number;
    failedExplorationNodeCount: number;
    cachedInputTokenCount: number;
    lastPhaseStatus?: string | null;
    failed: boolean;
}

export type KboFlowRunStatusPresentation = {
    color: "default" | "error" | "info" | "success" | "warning";
    isCompleted: boolean;
    label: string;
};

/**
 * Presents the persisted run outcome when it exists. Older rows did not retain
 * that field, so they keep the prior phase/outcome-message fallback.
 */
export function getKboFlowRunStatusPresentation(
    run: Pick<
        KboFlowRunSummary,
        "failed" | "lastPhaseStatus" | "outcomeMessage" | "status"
    >,
): KboFlowRunStatusPresentation {
    switch (run.status?.trim().toLowerCase()) {
        case "completed":
            return { color: "success", isCompleted: true, label: "Completed" };
        case "failed":
            return { color: "error", isCompleted: false, label: "Failed" };
        case "running":
            return { color: "info", isCompleted: false, label: "Running" };
        case "bookmarked":
            return { color: "warning", isCompleted: false, label: "Bookmarked" };
        default:
            return {
                color: run.failed ? "error" : "success",
                isCompleted: false,
                label: run.outcomeMessage || run.lastPhaseStatus || "—",
            };
    }
}

/** One page of run summaries plus the total row count for the pager. */
export interface KboFlowRunsPage {
    totalCount: number;
    page: number;
    pageSize: number;
    runs: KboFlowRunSummary[];
}

export interface KboFlowRunOutput {
    runId?: string;
    startedAtUtc?: string;
    completedAtUtc?: string;
    request?: KboFlowRunRequest;
    assembledContext?: KboAssembledContext;
    phaseRecords?: KboPhaseRecord[];
    generatedQuestions?: KboResearchQuestion[];
    validatedQuestions?: KboResearchQuestion[];
    pursuedQuestions?: KboResearchQuestion[];
    analysisResults?: KboMultiLevelAnalysisResult[];
    analysisFindings?: KboAnalysisFinding[];
    refinementResults?: KboCohortRefinementResult[];
    reports?: KboReport[];
    committedHistory?: KboCommittedHistory;
    /** Version 5+ multi-level exploration tree. Absent on older runs. */
    exploration?: KboExploration;
}

export interface KboExploration {
    explorationId?: string;
    nodes?: KboExplorationNode[];
}

export interface KboExplorationNode {
    nodeId?: string;
    parentNodeId?: string;
    rootQuestionId?: string;
    runId?: string;
    depth?: number;
    status?: string;
    createdAtUtc?: string;
    startedAtUtc?: string;
    completedAtUtc?: string;
    idempotencyKey?: string;
    /** Present when the node failed. */
    error?: string;
    /** Absent on the root node. */
    focus?: KboNodeFocus;
    /** Absent on failed nodes. */
    analysis?: KboNodeAnalysis;
    invocations?: KboAgentInvocation[];
}

export interface KboNodeFocus {
    parentClaim?: string;
    /** Version 7 link to the result that spawned this branch. */
    parentResultId?: string;
    parentFindingId?: string;
    stableFocusKey?: string;
    /** Cohort definition the node started from; rendered as raw JSON. */
    startCohort?: unknown;
    startCohortCount?: number;
    establishedFindings?: string[];
    establishedResults?: string[];
}

export interface KboNodeAnalysis {
    dimension?: {
        alias?: string;
        reason?: string;
        considered?: string[];
        requiredFilters?: unknown[];
    };
    /** Cohort that was analyzed; rendered as raw JSON. */
    analyzedCohort?: unknown;
    analyzedCohortCount?: number;
    passId?: string;
    results?: KboNodeAnalysisResult[];
    selected?: KboNodeSelectedFinding[];
    visuals?: KboNodeVisual[];
    establishedFindings?: string[];
    establishedResults?: string[];
}

export interface KboNodeFinding {
    /** Version 7 stable identity for an analysis result. */
    resultId?: string;
    name?: string;
    claim?: string;
    reasoning?: string;
    findingId?: string;
    passId?: string;
    conversationCount?: number;
    /** Fraction (0..1) of reviewed conversations supporting the finding. */
    conversationShare?: number;
    totalConversationsReviewed?: number;
    representativeConversationIds?: string[];
    /** Version 7 exact subset of reviewed material that exhibited this result. */
    matchingConversationIds?: string[];
}

export interface KboNodeAnalysisResult {
    cohort?: KboCohortDefinition;
    /** Version 7 results produced for this segment. */
    results?: KboNodeFinding[];
    findings?: KboNodeFinding[];
    /** Distribution over the analyzed dimension; rendered as raw JSON. */
    distribution?: unknown;
    reasoningModelName?: string;
    reviewedMaterialCount?: number;
    /** Fraction (0..1) of the cohort's material that was reviewed. */
    reviewedMaterialCoverage?: number;
    materialSource?: string;
    analysisMaterial?: unknown;
    undetermined?: unknown;
}

export interface KboNodeSelectedFinding {
    /** Version 7 selected analysis result. */
    result?: KboNodeFinding;
    finding?: KboNodeFinding;
    reason?: string;
    /** Child focus this finding spawned; rendered as raw JSON. */
    childFocus?: KboNodeFocus;
    exploreDeeper?: boolean;
}

export interface KboNodeVisual {
    visualId?: string;
    kind?: string;
    title?: string;
    caption?: string;
    /** Pre-rendered HTML markup. */
    markup?: string;
}

export interface KboFlowRunRequest {
    mode?: string;
    tenantId?: string;
    subjectUserId?: string;
    startDateUtc?: string;
    endDateUtc?: string;
    parameters?: {
        targetQuestionCount?: number;
        maxQuestionsToPursue?: number;
        ignoreHistory?: boolean;
    };
    modelId?: string | null;
    replaySourceRunId?: string | null;
    rerunFromPhase?: string | null;
}

export interface KboAssembledContext {
    identity?: Record<string, unknown>;
    tenantProfile?: Record<string, unknown>;
    userProfile?: Record<string, unknown>;
    permissions?: Record<string, unknown>;
    classifierCatalog?: Record<string, unknown>;
    signalSnapshot?: Record<string, unknown>;
    hierarchies?: Record<string, unknown>;
    generationHistory?: KboGenerationHistory;
    sectionStatuses?: KboContextSectionStatus[];
    sources?: KboContextSource[];
    snapshotText?: string;
}

export interface KboContextSectionStatus {
    section?: string;
    availability?: string;
    rowCount?: number;
    sourceReferences?: Array<Record<string, unknown>>;
}

export interface KboContextSource {
    section?: string;
    provider?: string;
    operation?: string;
    rowCount?: number;
    isMock?: boolean;
    parameters?: Record<string, unknown>;
}

export interface KboToolCall {
    name?: string;
    status?: string;
    /** JSON-encoded string of the arguments the agent passed to the tool. */
    argumentsJson?: string;
    /** JSON-encoded string of the tool result (may decode to a string, array, or object). */
    resultJson?: string;
    startedAtUtc?: string;
    completedAtUtc?: string;
}

export interface KboAgentInvocation {
    label?: string;
    /** Questions this call informed; generation calls can legitimately be linked to several questions. */
    questionIds?: string[];
    model?: string;
    deployment?: string;
    status?: string;
    systemPrompt?: string;
    userPrompt?: string;
    /** The agent's response. May be a plain string or a structured JSON object. */
    response?: unknown;
    error?: string;
    inputTokens?: number;
    cachedInputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
    startedAtUtc?: string;
    completedAtUtc?: string;
    /** Tool/function calls the agent made on this turn, each bundling its arguments and result. */
    toolCalls?: KboToolCall[];
}

export interface KboPhaseRecord {
    phase?: string;
    status?: string;
    executionModel?: string;
    inputSummary?: string;
    outputSummary?: string;
    startedAtUtc?: string;
    completedAtUtc?: string;
    invocations?: KboAgentInvocation[];
}

export interface KboResearchQuestion {
    id?: string;
    text?: string;
    rationale?: string;
    rejectionReason?: string;
    validation?: KboQuestionValidation;
}

export interface KboScoredDimension {
    score?: number;
    reason?: string;
}

export interface KboGateResult {
    passed?: boolean;
    reason?: string;
}

export interface KboQuestionValidation {
    accepted?: boolean;
    reason?: string;
    relationship?: string;
    scope?: string;
    target?: string;
    lever?: string;
    alignment?: KboScoredDimension;
    actionability?: KboScoredDimension;
    feasibility?: KboGateResult;
    specificity?: KboGateResult;
    estimatedCohortShare?: number;
}

export interface KboEvidenceQuote {
    conversationId?: string;
    text?: string;
}

export interface KboCohortFilter {
    alias?: string;
    operator?: string;
    value?: unknown;
    reason?: string;
}

export interface KboCohortDimension {
    alias?: string;
    outputAlias?: string | null;
    reason?: string;
}

export interface KboCohortMeasure {
    alias?: string;
    aggregate?: string;
    outputAlias?: string | null;
    reason?: string;
}

export interface KboCohortDefinition {
    name?: string;
    role?: string;
    filters?: KboCohortFilter[];
    dimensions?: KboCohortDimension[];
    measures?: KboCohortMeasure[];
    startDateUtc?: string;
    endDateUtc?: string;
    rationale?: string;
}

export interface KboAnalysisMaterial {
    conversationId?: string;
    text?: string;
}

export interface KboAnalysisDistribution {
    value?: unknown;
    count?: number;
    share?: number;
}

export interface KboMultiLevelFinding {
    findingId?: string;
    passId?: string;
    claim?: string;
    reasoning?: string;
    representativeConversationIds?: string[];
}

export interface KboAnalysisDimension {
    alias?: string;
    requiredFilters?: KboCohortFilter[];
    reason?: string;
}

export interface KboAnalysisDimensionResult {
    cohort?: KboCohortDefinition;
    distribution?: KboAnalysisDistribution;
    analysisMaterial?: KboAnalysisMaterial[];
    reviewedMaterialCount?: number;
    reviewedMaterialCoverage?: number;
    findings?: KboMultiLevelFinding[];
}

export interface KboAnalysisPassSelection {
    findingId?: string;
    shouldContinue?: boolean;
    reason?: string;
}

export interface KboMultiLevelAnalysisPass {
    passId?: string;
    level?: number;
    analysisScope?: string | null;
    analyzedCohort?: KboCohortDefinition;
    analyzedCohortCount?: number;
    dimension?: KboAnalysisDimension;
    results?: KboAnalysisDimensionResult[];
    selection?: KboAnalysisPassSelection;
}

export interface KboMultiLevelAnalysisResult {
    runId?: string;
    question?: KboResearchQuestion;
    initialCohort?: KboCohortDefinition;
    initialCohortCount?: number;
    passes?: KboMultiLevelAnalysisPass[];
    finalCohort?: KboCohortDefinition;
    finalFinding?: KboMultiLevelFinding;
    completionReason?: string;
    context?: Record<string, unknown>;
    /** The `maxDepth` this run was executed with. Absent on runs persisted before this field was added. */
    maxPasses?: number;
}

export interface KboResolvedCohort {
    questionId?: string;
    cohort?: KboCohortDefinition;
    matchingConversationCount?: number;
    relaxationCount?: number;
    meetsMinimum?: boolean;
}

export interface KboAnalysisFinding {
    questionId?: string;
    level?: string;
    cohort?: KboResolvedCohort;
    quotes?: KboEvidenceQuote[];
    patterns?: string[];
    toolsUsed?: string[];
    conversationIds?: string[];
    macroQueries?: Array<Record<string, unknown>>;
    candidateCohorts?: KboCohortDefinition[];
    macroFacts?: Array<Record<string, unknown>>;
    segments?: Array<Record<string, unknown>>;
    conversationEvidence?: Array<Record<string, unknown>>;
    patternFindings?: Array<Record<string, unknown>>;
    limitations?: string[];
}

export interface KboGeneratedCohort {
    filters?: KboCohortFilter[];
    reasoning?: string;
}

export interface KboCohortRefinementDistributionRow {
    value?: string;
    count?: number;
    share?: number;
}

export interface KboCohortRefinementPass {
    selectedDimension?: {
        alias?: string;
        requiredFilters?: KboCohortFilter[];
        evidenceModelNames?: string[];
        reasoning?: string;
    };
    distribution?: KboCohortRefinementDistributionRow[];
    evidence?: Array<Record<string, unknown>>;
    selectedRow?: KboCohortRefinementDistributionRow;
    hypothesis?: string;
    refinedCohort?: KboGeneratedCohort;
    shouldContinue?: boolean;
    reasoning?: string;
    level?: number;
}

export interface KboCohortRefinementResult {
    questionId?: string;
    proposedCohort?: KboCohortDefinition;
    refinement?: {
        question?: KboResearchQuestion;
        initialCohort?: KboGeneratedCohort;
        passes?: KboCohortRefinementPass[];
        finalCohort?: KboGeneratedCohort;
        reviewedConversationCount?: number;
    };
}

export interface KboReportFinding {
    questionId?: string;
    questionText?: string;
    finding?: string;
    /** Stable link to the exploration finding that produced this report entry. */
    findingId?: string;
    level?: string;
    evidence?: KboEvidenceQuote[];
}

/** Structured report data persisted by current Kbo.Flow runs. */
export interface KboReportData {
    question?: string;
    conclusion?: string;
    keyFindings?: KboReportFindingContent[];
    summary?: KboReportSummary;
    /** Version 6+ presentation findings included in the emailed report. */
    findings?: KboReportFindingContent[];
}

export interface KboReportFindingContent {
    findingId?: string;
    /** The underlying analysis result represented by this email finding. */
    leafAnalysisResultId?: string;
    title?: string;
    digest?: string;
    whyItMatters?: string;
    action?: string;
    provenanceText?: string;
    denominator?: number;
    reviewedMaterialCount?: number;
    strength?: string;
    visual?: KboReportVisual;
    howIGotHere?: string;
    explorationPath?: KboReportExplorationPathStep[];
}

export interface KboReportSummary {
    takeaway?: string;
    scope?: {
        category?: string;
        cohort?: string;
        conversationsAnalyzed?: number;
        totalConversationsInWindow?: number;
        window?: string;
        triggeringSignal?: {
            name?: string;
            kindLabel?: string;
            volume?: number;
            measurements?: Array<{
                label?: string;
                kind?: string;
                value?: number;
                comparisonValue?: number;
                comparisonLabel?: string;
            }>;
        };
    };
}

export interface KboReportVisual {
    visualId?: string;
    kind?: string;
    displayLabel?: string;
    reason?: string;
    caption?: string;
    markup?: string;
}

export interface KboReportExplorationPathStep {
    explorationNodeId?: string;
    parentNodeId?: string;
    passId?: string;
    resultId?: string;
    dimension?: string;
    segment?: string;
    analyzedCohortCount?: number;
    segmentConversationCount?: number;
    reviewedMaterialCount?: number;
    materialSource?: string;
}

export interface KboReportEmail {
    subject?: string;
    previewText?: string;
    fromDisplayName?: string;
    fromAddress?: string;
    /** Fully-rendered HTML document for the report email — the actual generated report. */
    htmlBody?: string;
    plainTextBody?: string;
}

export interface KboReport {
    /** Persisted KboFlow email report ID used for delivery and send-log attribution. */
    reportId?: string;
    title?: string;
    analysisRunId?: string;
    questionId?: string;
    /** Version 7 root for this report's exploration tree. */
    rootExplorationNodeId?: string;
    findings?: KboReportFinding[];
    data?: KboReportData;
    recommendedActions?: string[];
    email?: KboReportEmail;
}

export interface KboPriorRunQuestion {
    questionId?: string;
    text?: string;
    rationale?: string;
    leverageScore?: number;
    outcome?: string;
    resolutionSummary?: string;
    rejectionReason?: string;
}

export interface KboPriorRun {
    runId?: string;
    runCompletedAtUtc?: string;
    questions?: KboPriorRunQuestion[];
}

export interface KboGenerationHistory {
    priorRuns?: KboPriorRun[];
}

export type KboCommittedHistory = KboPriorRun;
